import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  createRequestDiscoveryRequest,
  getPopularRequestDiscovery,
  getRequestDiscoveryRequests,
  getRequestDiscoverySettings,
	retryContentRequest,
  searchRequestDiscovery,
} from "@/api/endpoints";
import { ApiError } from "@/api/client";
import { useApi } from "@/hooks/useApi";
import { RatingBadge } from "@/components/RatingBadge";
import type { ContentRequest, ContentRequestAttributes, RequestDiscoveryResult, RequestResolution } from "@/types/api";

type RequestMediaType = ContentRequest["attributes"]["media_type"];

const RESOLUTION_OPTIONS: Array<{ value: RequestResolution; label: string; rank: number }> = [
  { value: "sd", label: "SD", rank: 1 },
  { value: "720p", label: "720p", rank: 2 },
  { value: "1080p", label: "1080p", rank: 3 },
  { value: "4k", label: "4K", rank: 4 },
];
const DEFAULT_POPULAR_LIMIT = 10;
const EXPANDED_POPULAR_LIMIT = 50;

export function RequestsPage() {
  const requests = useApi(() => getRequestDiscoveryRequests(), []);
  const discoverySettings = useApi(() => getRequestDiscoverySettings(), []);
  const [popularMovieLimit, setPopularMovieLimit] = useState(DEFAULT_POPULAR_LIMIT);
  const [popularSeriesLimit, setPopularSeriesLimit] = useState(DEFAULT_POPULAR_LIMIT);
  const popularMovies = useApi(
    () => getPopularRequestDiscovery({ media_type: "movie", limit: popularMovieLimit }),
    [popularMovieLimit],
  );
  const popularSeries = useApi(
    () => getPopularRequestDiscovery({ media_type: "series", limit: popularSeriesLimit }),
    [popularSeriesLimit],
  );
  const [title, setTitle] = useState("");
  const [mediaType, setMediaType] = useState<RequestMediaType>("movie");
  const [resolutionOverrides, setResolutionOverrides] = useState<Record<string, RequestResolution>>({});
  const [searching, setSearching] = useState(false);
  const [searchComplete, setSearchComplete] = useState(false);
  const [updatingID, setUpdatingID] = useState<string | null>(null);
  const [searchResults, setSearchResults] = useState<RequestDiscoveryResult[]>([]);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const requestList = useMemo(() => requests.data?.data ?? [], [requests.data]);
  const hasInFlightRequests = useMemo(
    () => requestList.some((req) => {
      const state = req.attributes.arr_state;
      const waitingForLocalItem = (state === "ready" || state === "ready_unlinked") && !req.relationships.item?.data;
      return state === "downloading"
        || state === "queued"
        || state === "importing"
        || state === "searching"
        || state === "partial"
        || state === "stalled"
        || waitingForLocalItem;
    }),
    [requestList],
  );

  useEffect(() => {
    if (!hasInFlightRequests) return;
    const interval = window.setInterval(() => {
      void requests.refetch();
    }, 15000);
    return () => window.clearInterval(interval);
  }, [hasInFlightRequests, requests]);

  const defaultResolution = mediaType === "series"
    ? discoverySettings.data?.data.attributes.series_default_resolution ?? "1080p"
    : discoverySettings.data?.data.attributes.movie_default_resolution ?? "1080p";
  const maxResolution = mediaType === "series"
    ? discoverySettings.data?.data.attributes.series_max_resolution ?? "1080p"
    : discoverySettings.data?.data.attributes.movie_max_resolution ?? "1080p";
  const availableResolutions = resolutionOptionsUpTo(maxResolution);
  const defaultMovieResolution = discoverySettings.data?.data.attributes.movie_default_resolution ?? "1080p";
  const defaultSeriesResolution = discoverySettings.data?.data.attributes.series_default_resolution ?? "1080p";
  const movieResolutions = resolutionOptionsUpTo(discoverySettings.data?.data.attributes.movie_max_resolution ?? "1080p");
  const seriesResolutions = resolutionOptionsUpTo(discoverySettings.data?.data.attributes.series_max_resolution ?? "1080p");

  async function handleSearchDiscovery() {
    const trimmedTitle = title.trim();
    if (!trimmedTitle) return;
    setSearching(true);
    setSearchComplete(false);
    setMessage(null);
    setError(null);
    try {
      await refreshDiscoveryResults(true);
    } catch {
      setError("External discovery search could not be completed.");
    } finally {
      setSearching(false);
    }
  }

  async function refreshDiscoveryResults(showEmptyMessage: boolean) {
    const response = await searchRequestDiscovery({ q: title.trim(), media_type: mediaType, limit: 10 });
    setSearchResults(response.data);
    setSearchComplete(true);
    if (showEmptyMessage && response.data.length === 0) {
      setMessage("No external matches found.");
    }
  }

  async function handleRequestResult(result: RequestDiscoveryResult) {
    if (!result.attributes.can_request) return;
    setUpdatingID(result.id);
    setMessage(null);
    setError(null);
    try {
      const requestBody: Parameters<typeof createRequestDiscoveryRequest>[0] = {
        title: result.attributes.title,
        media_type: result.attributes.media_type,
        source: result.attributes.source,
        external_id: result.attributes.external_id,
        resolution: resolutionOverrides[result.id] ?? defaultResolutionFor(result.attributes.media_type),
      };
      await createRequestDiscoveryRequest(requestBody);
      setMessage(`${result.attributes.title} requested.`);
      await requests.refetch();
      if (searchComplete) {
        await refreshDiscoveryResults(false);
      }
      if (result.attributes.media_type === "series") {
        await popularSeries.refetch();
      } else if (result.attributes.media_type === "movie") {
        await popularMovies.refetch();
      }
    } catch (err) {
      if (err instanceof ApiError && err.problem.code === "acquisition_failed") {
        setError(err.problem.detail || "Request could not be submitted.");
        await requests.refetch();
        if (searchComplete) {
          try {
            await refreshDiscoveryResults(false);
          } catch {
            // The persisted failed request is the important state to reveal here.
          }
        }
        if (result.attributes.media_type === "series") {
          await popularSeries.refetch();
        } else if (result.attributes.media_type === "movie") {
          await popularMovies.refetch();
        }
      } else {
        setError("Request could not be submitted.");
      }
    } finally {
      setUpdatingID(null);
    }
  }

	async function handleRetryRequest(request: ContentRequest) {
		setUpdatingID(request.id);
		setMessage(null);
		setError(null);
		try {
			await retryContentRequest(request.id);
			setMessage(`${request.attributes.title} requested again.`);
			await requests.refetch();
		} catch (err) {
			setError(err instanceof ApiError ? err.problem.detail : "Request could not be retried.");
		} finally {
			setUpdatingID(null);
		}
	}

  function defaultResolutionFor(requestMediaType: RequestMediaType) {
    return requestMediaType === "series" ? defaultSeriesResolution : defaultMovieResolution;
  }

  return (
    <div className="requests-page">
      <header className="page-header">
        <div>
          <h1>Requests</h1>
          <p className="settings-copy">
            Search for movies and series, then request the result you want.
          </p>
        </div>
      </header>

      {message && <p className="settings-status settings-status--success">{message}</p>}
      {error && <p className="settings-status settings-status--error">{error}</p>}

      <section className="requests-layout">
        <section className="requests-discovery">
          <form
            className="requests-search-panel"
            onSubmit={(event) => {
              event.preventDefault();
              void handleSearchDiscovery();
            }}
          >
            <label>
              <span>Search</span>
              <div className="requests-search-row">
                <input
                  value={title}
                  onChange={(event) => {
                    setTitle(event.target.value);
                    setSearchResults([]);
                    setSearchComplete(false);
                  }}
                  placeholder="Search movies or shows"
                />
                <select
                  value={mediaType}
                  onChange={(event) => {
                    setMediaType(event.target.value as RequestMediaType);
                    setResolutionOverrides({});
                    setSearchResults([]);
                    setSearchComplete(false);
                  }}
                >
                  <option value="movie">Movie</option>
                  <option value="series">Series</option>
                </select>
                <button className="btn btn--primary" type="submit" disabled={searching || !title.trim()}>
                  {searching ? "Searching..." : "Search"}
                </button>
              </div>
            </label>
          </form>

          {searchComplete && searchResults.length === 0 && (
            <div className="requests-empty">
              No matches found.
            </div>
          )}
          {searchResults.length > 0 && (
            <DiscoveryResultList
              results={searchResults}
              defaultResolution={defaultResolution}
              availableResolutions={availableResolutions}
              resolutionOverrides={resolutionOverrides}
              updatingID={updatingID}
              onResolutionChange={(resultID, resolution) => {
                setResolutionOverrides((current) => ({ ...current, [resultID]: resolution }));
              }}
              onRequest={(result) => void handleRequestResult(result)}
            />
          )}

          <section className="requests-popular-section" aria-label="Popular movies">
            <div className="requests-section-header">
              <h2>Popular Movies</h2>
              <div className="requests-section-header-actions">
                {popularMovies.loading && <span>Loading...</span>}
              </div>
            </div>
            {popularMovies.error && (
              <p className="requests-empty">Popular movies could not be loaded.</p>
            )}
            {!popularMovies.loading && !popularMovies.error && (popularMovies.data?.data.length ?? 0) === 0 && (
              <p className="requests-empty">No popular movies available.</p>
            )}
            {(popularMovies.data?.data.length ?? 0) > 0 && (
              <DiscoveryResultList
                results={popularMovies.data?.data ?? []}
                defaultResolution={defaultMovieResolution}
                availableResolutions={movieResolutions}
                resolutionOverrides={resolutionOverrides}
                updatingID={updatingID}
                onResolutionChange={(resultID, resolution) => {
                  setResolutionOverrides((current) => ({ ...current, [resultID]: resolution }));
                }}
                onRequest={(result) => void handleRequestResult(result)}
              />
            )}
            {popularMovieLimit < EXPANDED_POPULAR_LIMIT && (popularMovies.data?.data.length ?? 0) > 0 && (
              <div className="requests-section-footer">
                <button
                  className="btn btn--secondary btn--compact"
                  type="button"
                  disabled={popularMovies.loading}
                  onClick={() => setPopularMovieLimit(EXPANDED_POPULAR_LIMIT)}
                >
                  More
                </button>
              </div>
            )}
          </section>

          <section className="requests-popular-section" aria-label="Popular series">
            <div className="requests-section-header">
              <h2>Popular Series</h2>
              <div className="requests-section-header-actions">
                {popularSeries.loading && <span>Loading...</span>}
              </div>
            </div>
            {popularSeries.error && (
              <p className="requests-empty">Popular series could not be loaded.</p>
            )}
            {!popularSeries.loading && !popularSeries.error && (popularSeries.data?.data.length ?? 0) === 0 && (
              <p className="requests-empty">No popular series available.</p>
            )}
            {(popularSeries.data?.data.length ?? 0) > 0 && (
              <DiscoveryResultList
                results={popularSeries.data?.data ?? []}
                defaultResolution={defaultSeriesResolution}
                availableResolutions={seriesResolutions}
                resolutionOverrides={resolutionOverrides}
                updatingID={updatingID}
                onResolutionChange={(resultID, resolution) => {
                  setResolutionOverrides((current) => ({ ...current, [resultID]: resolution }));
                }}
                onRequest={(result) => void handleRequestResult(result)}
              />
            )}
            {popularSeriesLimit < EXPANDED_POPULAR_LIMIT && (popularSeries.data?.data.length ?? 0) > 0 && (
              <div className="requests-section-footer">
                <button
                  className="btn btn--secondary btn--compact"
                  type="button"
                  disabled={popularSeries.loading}
                  onClick={() => setPopularSeriesLimit(EXPANDED_POPULAR_LIMIT)}
                >
                  More
                </button>
              </div>
            )}
          </section>
        </section>

        <section className="requests-history" aria-label="Recent requests">
          <h2>Recent Requests</h2>
          {requests.loading && requests.data === null && <p className="settings-copy">Loading requests...</p>}
          {requests.error && <p className="settings-status settings-status--error">Requests could not be loaded.</p>}
          {requests.data !== null && !requests.error && requestList.length === 0 && (
            <p className="requests-empty">No requests yet.</p>
          )}
          {requestList.length > 0 && (
            <div className="requests-list">
              {requestList.slice(0, 5).map((request) => (
                <RequestQueueItem
                  key={request.id}
                  request={request}
				  retrying={updatingID === request.id}
				  onRetry={() => void handleRetryRequest(request)}
                />
              ))}
            </div>
          )}
          {requestList.length > 5 && (
            <Link className="btn btn--ghost requests-show-all" to="/requests/all">
              Show all {requestList.length} requests
            </Link>
          )}
        </section>
      </section>
    </div>
  );
}

function DiscoveryResultList({
  results,
  defaultResolution,
  availableResolutions,
  resolutionOverrides,
  updatingID,
  onResolutionChange,
  onRequest,
}: {
  results: RequestDiscoveryResult[];
  defaultResolution: RequestResolution;
  availableResolutions: Array<{ value: RequestResolution; label: string }>;
  resolutionOverrides: Record<string, RequestResolution>;
  updatingID: string | null;
  onResolutionChange: (resultID: string, resolution: RequestResolution) => void;
  onRequest: (result: RequestDiscoveryResult) => void;
}) {
  return (
    <div className="request-discovery-results" aria-label="External discovery results">
      {results.map((result) => {
        const attrs = result.attributes;
        const blockedLabel = attrs.available ? "In Library" : attrs.already_requested ? "Requested" : "";
        const localItem = attrs.items[0] ?? null;
        return (
          <article key={result.id} className="request-discovery-result">
            <div className="request-discovery-poster">
              {attrs.poster_url ? <img src={attrs.poster_url} alt="" /> : <span>{mediaTypeLabel(attrs.media_type)}</span>}
            </div>
            <div className="request-discovery-result-main">
              <div>
                <h3>
                  {attrs.tmdb_url ? (
                    <a href={attrs.tmdb_url} target="_blank" rel="noreferrer">
                      {attrs.title}
                    </a>
                  ) : attrs.title}
                </h3>
                <p className="request-discovery-meta">
                  {mediaTypeLabel(attrs.media_type)}
                  {attrs.year ? ` · ${attrs.year}` : ""}
                </p>
              </div>
              {attrs.availability_label && (
                <span className={`request-availability request-availability--${attrs.availability_status ?? "unknown"}`}>
                  {attrs.availability_label}
                </span>
              )}
              {hasScore(attrs.rating_average) && (
                <RatingBadge
                  placement="detail"
                  rating={{
                    score: attrs.rating_average,
                    votes: attrs.rating_votes ?? null,
                    source: attrs.rating_source ?? "tmdb",
                  }}
                />
              )}
              {attrs.overview && <p className="request-discovery-overview">{attrs.overview}</p>}
              <div className="request-discovery-links">
                {attrs.available && localItem ? (
                  <Link className="request-discovery-link" to={`/items/${localItem.id}`}>
                    Open local item
                  </Link>
                ) : (
                  attrs.tmdb_url && (
                    <a
                      className="request-discovery-link"
                      href={attrs.tmdb_url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      More info on TMDb ↗
                    </a>
                  )
                )}
              </div>
            </div>
            <div className="request-discovery-actions">
              {(attrs.media_type === "movie" || attrs.media_type === "series") && attrs.can_request && (
                <label className="request-resolution-select">
                  <span>Resolution</span>
                  <select
                    value={resolutionOverrides[result.id] ?? defaultResolution}
                    onChange={(event) => onResolutionChange(result.id, event.target.value as RequestResolution)}
                  >
                    {availableResolutions.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </label>
              )}
              <button
                className={`btn btn--compact ${attrs.can_request ? "btn--primary" : "btn--secondary"}`}
                type="button"
                disabled={!attrs.can_request || updatingID === result.id}
                onClick={() => onRequest(result)}
              >
                {updatingID === result.id ? "Requesting..." : blockedLabel || "Request"}
              </button>
            </div>
          </article>
        );
      })}
    </div>
  );
}

function hasScore(value: number | undefined | null): value is number {
  return typeof value === "number" && value > 0;
}

function resolutionOptionsUpTo(maxResolution: RequestResolution) {
  const maxRank = RESOLUTION_OPTIONS.find((option) => option.value === maxResolution)?.rank ?? 3;
  return RESOLUTION_OPTIONS.filter((option) => option.rank <= maxRank).map(({ value, label }) => ({ value, label }));
}

export function RequestQueueItem({ request, retrying = false, onRetry }: { request: ContentRequest; retrying?: boolean; onRetry?: () => void }) {
  const attrs = request.attributes;
  const localItem = request.relationships.item?.data ?? null;
  return (
    <article className="request-row">
      <div className="request-row-main">
        <div className="request-row-title">
          <h3>
            {localItem ? (
              <Link to={`/items/${localItem.id}`}>{attrs.title}</Link>
            ) : (
              attrs.title
            )}
          </h3>
          {attrs.arr_state && (
            <span className={`arr-status arr-status--${attrs.arr_state}`}>
              {arrStateLabel(attrs.arr_state)}
              {arrStateShowsProgress(attrs.arr_state) && typeof attrs.arr_progress_percent === "number" && attrs.arr_progress_percent > 0 && (
                <span className="arr-status-progress"> {attrs.arr_progress_percent}%</span>
              )}
            </span>
          )}
        </div>
        {arrStateShowsProgress(attrs.arr_state) && typeof attrs.arr_progress_percent === "number" && attrs.arr_progress_percent > 0 && (
          <div className="arr-status-bar" aria-hidden="true">
            <div className="arr-status-bar-fill" style={{ width: `${attrs.arr_progress_percent}%` }} />
          </div>
        )}
        {arrStateShowsProgress(attrs.arr_state) && (attrs.arr_download_speed_bps || attrs.arr_eta_seconds || attrs.arr_size_bytes) && (
          <div className="arr-status-detail">
            {typeof attrs.arr_size_bytes === "number" && attrs.arr_size_bytes > 0 && (
              <span>{formatBytes((attrs.arr_size_bytes - (attrs.arr_size_left_bytes ?? 0)))} / {formatBytes(attrs.arr_size_bytes)}</span>
            )}
            {typeof attrs.arr_download_speed_bps === "number" && attrs.arr_download_speed_bps > 0 && (
              <span>{formatBytes(attrs.arr_download_speed_bps)}/s</span>
            )}
            {typeof attrs.arr_eta_seconds === "number" && attrs.arr_eta_seconds > 0 && (
              <span>ETA {formatDurationSeconds(attrs.arr_eta_seconds)}</span>
            )}
          </div>
        )}
        <div className="request-meta">
          <span>{mediaTypeLabel(attrs.media_type)}</span>
          {attrs.resolution && <span>{resolutionLabel(attrs.resolution)}</span>}
          {attrs.source && <span>{attrs.source}</span>}
          {attrs.external_id && <span>{attrs.external_id}</span>}
          <span>Requested {formatRequestDate(attrs.requested_at)}</span>
          {attrs.arr_message && <span>{attrs.arr_message}</span>}
        </div>
        {attrs.note && <p className="request-note">{attrs.note}</p>}
		{attrs.status === "failed" && onRetry && (
			<button className="btn btn--ghost" type="button" disabled={retrying} onClick={onRetry}>
				{retrying ? "Retrying..." : "Retry"}
			</button>
		)}
      </div>
    </article>
  );
}

function arrStateShowsProgress(state: ContentRequestAttributes["arr_state"]): boolean {
  return state === "downloading" || state === "importing" || state === "stalled";
}

function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  const precision = value >= 100 || unit === 0 ? 0 : value >= 10 ? 1 : 2;
  return `${value.toFixed(precision)} ${units[unit]}`;
}

function formatDurationSeconds(total: number): string {
  if (!Number.isFinite(total) || total <= 0) return "0s";
  const seconds = Math.floor(total);
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${secs}s`;
  return `${secs}s`;
}

function arrStateLabel(state: NonNullable<ContentRequestAttributes["arr_state"]>): string {
  switch (state) {
    case "ready":
      return "Ready";
    case "ready_unlinked":
      return "Ready, indexing";
    case "downloading":
      return "Downloading";
    case "queued":
      return "Queued";
    case "importing":
      return "Importing";
    case "stalled":
      return "Stalled";
    case "failed":
      return "Failed";
    case "searching":
      return "Searching";
    case "missing":
      return "Missing";
    case "partial":
      return "Partial";
    case "unavailable":
      return "Unavailable";
  }
}

function mediaTypeLabel(value: RequestMediaType) {
  switch (value) {
    case "movie":
      return "Movie";
    case "series":
      return "Series";
  }
}

function resolutionLabel(value: RequestResolution) {
  return RESOLUTION_OPTIONS.find((option) => option.value === value)?.label ?? value;
}

function formatRequestDate(value: string) {
  if (!value) return "recently";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}
